import * as React from "react";
import { useState, useCallback } from "react";
import styled from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import useProductName from "brand-customization/useProductName";
import HighlightingRulesContext from "views/components/contexts/HighlightingRulesContext";
import { DocumentTitle, Spinner, Link } from "components/common";
import PageContentLayout from "components/layout/PageContentLayout";
import useSearchConfiguration from "hooks/useSearchConfiguration";
import useActiveSearch from "data-lake/preview/hooks/useActiveSearch";
import DataLakePreviewStoreProvider from "data-lake/preview/store/DataLakePreviewStoreProvider";
import useUrlParams from "data-lake/preview/hooks/useUrlParams";
import usePermissions from "hooks/usePermissions";
import Panel from "components/bootstrap/Panel";
import DataLakeRoutes from "data-lake/Routes";
import useLicenseValidityForSubject from "license/hooks/useLicenseValidityForSubject";
import { LICENSE_SUBJECTS } from "license/constants";
import { InvalidLicenseWarning } from "license/LicenseCheck";
import Sidebar from "views/components/sidebar/Sidebar";
import DataLakeHighlightingRules from "data-lake/preview/DataLakeHighlightingRules";
import useActiveDataLakes from "data-lake/preview/hooks/useActiveDataLakes";
import PreviewConfiguredDataLakesProvider from "data-lake/preview/contexts/PreviewConfiguredDataLakesProvider";
import useSelectedDataLake from "data-lake/hooks/useSelectedDataLake";
import usePluginEntities from "hooks/usePluginEntities";
import FieldTypesProvider from "data-lake/preview/contexts/FieldTypesProvider";
import findDataLakeIdInSearchType from "data-lake/preview/utils/findDataLakeIdInSearchType";
import useFeature from "hooks/useFeature";
import { DocumentationLink } from "components/support";
import DocsHelper from "util/DocsHelper";
import AppConfig from "util/AppConfig";
import { DATA_LAKE_INTERNAL_ENABLED } from "data-lake/Constants";
const initialSearchForDataLake = (selectedDataLakeId, activeSearch) => {
  if (!selectedDataLakeId || !activeSearch) return void 0;
  const activeSearchDataLakeId = findDataLakeIdInSearchType(activeSearch);
  if (activeSearchDataLakeId === selectedDataLakeId && activeSearch) return activeSearch;
  return void 0;
};
const PreviewPageContent = ({
  activeSearch,
  initialUrlParams,
  searchJob,
  resetUrlQueryParams
}) => {
  const { selectedDataLake } = useSelectedDataLake();
  const dataLakePlugins = usePluginEntities("dataLakeStorageTypes");
  const dataLakePlugin = dataLakePlugins.find(({ storageType }) => storageType === (selectedDataLake == null ? void 0 : selectedDataLake.storageType));
  const PageComponent = dataLakePlugin == null ? void 0 : dataLakePlugin.previewComponent;
  const initialSearch = initialSearchForDataLake(selectedDataLake == null ? void 0 : selectedDataLake.id, activeSearch);
  if (!PageComponent) {
    throw new Error(`No page component found for data lake storage type: ${selectedDataLake == null ? void 0 : selectedDataLake.storageType}`);
  }
  return /* @__PURE__ */ React.createElement(
    PageComponent,
    {
      key: selectedDataLake == null ? void 0 : selectedDataLake.id,
      initialSearch,
      initialUrlParams,
      initialSearchJob: searchJob,
      resetUrlQueryParams
    }
  );
};
const StyledPageContentLayout = styled(PageContentLayout)`
  .page-content-grid {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    overflow: auto;
  }
`;
const GridContainer = styled.div`
  display: flex;
  height: 100%;
`;
const DataLakePreviewPageComponent = ({ activeDataLakes }) => {
  const { t } = useTranslation("enterprise_data_lake");
  const { initialUrlParams, resetUrlQueryParams, setDataLakeIdUrlParam } = useUrlParams();
  const { config } = useSearchConfiguration();
  const [highlightingRules, setHighlightingRules] = useState([]);
  const highlightSidebarSection = useCallback(
    () => /* @__PURE__ */ React.createElement(DataLakeHighlightingRules, { setHighlightingRules }),
    []
  );
  const {
    search: activeSearch,
    result: activeSearchResult,
    searchJob,
    isLoading: isLoadingActiveSearch
  } = useActiveSearch(initialUrlParams);
  return /* @__PURE__ */ React.createElement(DocumentTitle, { title: t("preview_page.title") }, config && !isLoadingActiveSearch ? /* @__PURE__ */ React.createElement(
    PreviewConfiguredDataLakesProvider,
    {
      setDataLakeIdUrlParam,
      initialUrlParams,
      activeSearch,
      searchJobStatus: searchJob.status,
      activeDataLakes
    },
    /* @__PURE__ */ React.createElement(DataLakePreviewStoreProvider, { result: activeSearchResult }, /* @__PURE__ */ React.createElement(FieldTypesProvider, null, /* @__PURE__ */ React.createElement(HighlightingRulesContext.Provider, { value: highlightingRules }, /* @__PURE__ */ React.createElement(GridContainer, null, /* @__PURE__ */ React.createElement(
      Sidebar,
      {
        sections: [
          {
            key: "highlighting",
            icon: "format_paragraph",
            title: t("preview_page.highlighting"),
            content: highlightSidebarSection
          }
        ],
        enableSidebarPinning: false,
        actions: [],
        title: t("preview_page.title")
      }
    ), /* @__PURE__ */ React.createElement(StyledPageContentLayout, null, /* @__PURE__ */ React.createElement(
      PreviewPageContent,
      {
        activeSearch,
        initialUrlParams,
        searchJob,
        resetUrlQueryParams
      }
    ))))))
  ) : /* @__PURE__ */ React.createElement(Spinner, null));
};
const LicenseCheck = ({ children }) => {
  const { t } = useTranslation("enterprise_data_lake");
  const {
    data: { valid: isValidLicense },
    isInitialLoading: isLoadingLicense
  } = useLicenseValidityForSubject(LICENSE_SUBJECTS.enterprise);
  if (isLoadingLicense) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  if (!isValidLicense) {
    return /* @__PURE__ */ React.createElement(PageContentLayout, null, /* @__PURE__ */ React.createElement(
      InvalidLicenseWarning,
      {
        title: t("preview_page.license_disabled_title"),
        featureName: t("preview_page.title"),
        licenseSubject: LICENSE_SUBJECTS.enterprise,
        displayWarningContainer: true,
        bsStyle: "danger"
      }
    ));
  }
  return children;
};
const SetupInstructions = ({
  hasPermissions,
  externalEnabled,
  internalEnabled
}) => {
  const { t } = useTranslation("enterprise_data_lake");
  const productName = useProductName();
  const backendLink = /* @__PURE__ */ React.createElement(Link, { to: DataLakeRoutes.BACKEND });
  const connectorsLink = /* @__PURE__ */ React.createElement(Link, { to: DataLakeRoutes.CONNECTORS });
  if (!hasPermissions) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, t("setup_instructions.no_permissions"));
  }
  const isCloud = AppConfig.isCloud();
  if (externalEnabled && internalEnabled) {
    return isCloud ? /* @__PURE__ */ React.createElement(
      Trans,
      {
        t,
        i18nKey: "setup_instructions.cloud_both",
        values: { productName },
        components: { connectors: connectorsLink }
      }
    ) : /* @__PURE__ */ React.createElement(
      Trans,
      {
        t,
        i18nKey: "setup_instructions.on_prem_both",
        components: { backend: backendLink, connectors: connectorsLink }
      }
    );
  }
  if (internalEnabled) {
    return isCloud ? /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "setup_instructions.cloud_internal", values: { productName } }) : /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "setup_instructions.on_prem_internal", components: { backend: backendLink } });
  }
  if (externalEnabled) {
    return /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "setup_instructions.external_only", components: { connectors: connectorsLink } });
  }
  return null;
};
const ActiveBackendCheck = ({ children }) => {
  const { t } = useTranslation("enterprise_data_lake");
  const { isPermitted } = usePermissions();
  const {
    data: activeDataLakes,
    isInitialLoading: isInitialLoadingActiveDataLakes,
    hasActiveDataLake
  } = useActiveDataLakes();
  const hasConfigPermissions = isPermitted("data_lake_config:update");
  const externalDLFeatureIsEnabled = useFeature("external_data_lake_search");
  const internalDLFeatureIsEnabled = DATA_LAKE_INTERNAL_ENABLED;
  if (isInitialLoadingActiveDataLakes) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  if (!hasActiveDataLake) {
    return /* @__PURE__ */ React.createElement(PageContentLayout, null, /* @__PURE__ */ React.createElement(Panel, { bsStyle: "info" }, /* @__PURE__ */ React.createElement(Panel.Heading, null, t("preview_page.no_backend_title")), /* @__PURE__ */ React.createElement(Panel.Body, null, /* @__PURE__ */ React.createElement(
      Trans,
      {
        t,
        i18nKey: "preview_page.no_backend_text",
        components: {
          setup: /* @__PURE__ */ React.createElement(
            SetupInstructions,
            {
              hasPermissions: hasConfigPermissions,
              externalEnabled: externalDLFeatureIsEnabled,
              internalEnabled: internalDLFeatureIsEnabled
            }
          ),
          docsLink: /* @__PURE__ */ React.createElement(DocumentationLink, { page: DocsHelper.PAGES.DATA_LAKE, text: t("preview_page.documentation") })
        }
      }
    ))));
  }
  return children({ activeDataLakes });
};
const DataLakePreviewPage = () => /* @__PURE__ */ React.createElement(LicenseCheck, null, /* @__PURE__ */ React.createElement(ActiveBackendCheck, null, ({ activeDataLakes }) => /* @__PURE__ */ React.createElement(DataLakePreviewPageComponent, { activeDataLakes })));
export default DataLakePreviewPage;
